import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaInstagram,
  FaLinkedinIn,
  FaFacebookF,
  FaPaperPlane,
  FaRocket,
  FaCheckCircle,
} from 'react-icons/fa';
import './Contact.css';

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0 },
};

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const msg = `Hi Amresh! I want to discuss a project.

Name / Company: ${form.name}
Email: ${form.email}
Phone: ${form.phone}
Service Required: ${form.service}

Project Details:
${form.message}`;

    const url = `https://api.whatsapp.com/send/?phone=919004068462&text=${encodeURIComponent(msg)}`;

    setSubmitted(true);
    window.open(url, '_blank');

    setTimeout(() => setSubmitted(false), 4500);
  };

  return (
    <>
      <Helmet>
        <title>Contact Amresh Prajapati | Hire Web Developer in Mumbai</title>
        <meta
          name="description"
          content="Contact Amresh Prajapati to hire a freelance website developer in Mumbai. Call or WhatsApp +91 90040 68462 for website design, React, WordPress, SEO projects. Free quotes, reply within 24 hours."
        />
        <meta
          name="keywords"
          content="contact Amresh Prajapati, hire website developer Mumbai, freelance web developer Mumbai, website designer Virar, React developer Mumbai, WordPress developer Mumbai"
        />
        <link rel="canonical" href="https://freelanceramreshprajapatimumbai.in/contact" />
      </Helmet>

      <section className="contactHero">
        <div className="contactHero__glow contactHero__glow--one" />
        <div className="contactHero__glow contactHero__glow--two" />

        <div className="contactContainer contactHero__inner">
          <motion.div
            className="contactHero__content"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.65 }}
          >
            <span className="contactTag">
              <FaRocket /> Contact
            </span>

            <h1>
              Let’s Build Something <span>Great Together</span>
            </h1>

            <p>
              Have a website idea, business website, portfolio, landing page or custom React project?
              Share your requirement and I’ll connect with you quickly.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="contactSection">
        <div className="contactContainer contact-layout">
          <motion.div
            className="contact-info"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="contactTag">
              <FaCheckCircle /> Quick Response
            </span>

            <h2>
              Get in <span>Touch</span>
            </h2>

            <p className="contact-info__desc">
              Need a premium website, portfolio, landing page, SEO-ready business website
              or custom React project? Fill the form and your details will be sent directly
              to my WhatsApp for a faster reply.
            </p>

            <div className="contact-items">
              <a href="tel:+919004068462" className="contact-item">
                <div className="contact-item__icon"><FaPhone /></div>
                <div>
                  <strong>Phone</strong>
                  <span>+91 9004068462</span>
                </div>
              </a>

              <a href="mailto:info@freelanceramreshprajapatimumbai.in" className="contact-item">
                <div className="contact-item__icon"><FaEnvelope /></div>
                <div>
                  <strong>Email</strong>
                  <span>info@freelanceramreshprajapatimumbai.in</span>
                </div>
              </a>

              <a
                href="https://maps.google.com/?q=Amresh+Prajapati,+FRF4%2B572,+Rustomjee+Ave,+Evershine+Global+City,+Dongarpada,+Virar+West,+Vasai-Virar,+Maharashtra+401303"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-item"
              >
                <div className="contact-item__icon"><FaMapMarkerAlt /></div>
                <div>
                  <strong>Location</strong>
                  <span>Mumbai, Maharashtra</span>
                </div>
              </a>

              <a
                href="https://api.whatsapp.com/send/?phone=919004068462"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-item contact-item--whatsapp"
              >
                <div className="contact-item__icon"><FaWhatsapp /></div>
                <div>
                  <strong>WhatsApp</strong>
                  <span>Chat directly on WhatsApp</span>
                </div>
              </a>
            </div>

            <div className="contact-socials">
              <p>Follow me on social media</p>

              <div className="contact-socials__icons">
                <a
                  href="https://www.instagram.com/amreshprajapatiwebdevloper/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </a>

                <a
                  href="https://www.linkedin.com/in/amresh-prajapati/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn />
                </a>

                <a
                  href="https://www.facebook.com/amreshprajapatiwebdevloper"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FaFacebookF />
                </a>

                <a
                  href="https://api.whatsapp.com/send/?phone=919004068462"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp />
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="contact-form-wrap"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.12 }}
          >
            <div className="contact-form-card">
              <span className="contact-form-badge">
                <FaWhatsapp /> WhatsApp Enquiry
              </span>

              <h3>Let’s Build Your Website</h3>

              <p className="contact-form-sub">
                Share your requirement and I’ll redirect your enquiry directly to WhatsApp
                for a faster reply.
              </p>

              {submitted && (
                <div className="contact-success">
                  ✅ Opening WhatsApp... your project enquiry is ready to send.
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label>Full Name / Company Name *</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your name or company name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="example@email.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+91 90045 63257"
                      value={form.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>What Are You Looking For? *</label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="">— Select a Service —</option>
                    <option value="Website Development">Website Development</option>
                    <option value="Portfolio Website">Portfolio Website</option>
                    <option value="Landing Page">Landing Page</option>
                    <option value="React / Custom Development">React / Custom Development</option>
                    <option value="WordPress Website">WordPress Website</option>
                    <option value="SEO / Marketing / Branding">SEO / Marketing / Branding</option>
                    <option value="Domain / Hosting">Domain / Hosting</option>
                    <option value="Packaging Design">Packaging Design</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Project Details *</label>
                  <textarea
                    name="message"
                    placeholder="Tell me about your project, pages, budget, timeline or reference website..."
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <button type="submit" className="contact-submit">
                  <FaPaperPlane /> Contact Now on WhatsApp
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Contact;